import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useFarmerRegistration from "../hooks/useFarmerRegistration";
import "../styles/Form.css";
import ProvinceSelectModal from "./Modals/ProvinceSelectModal";
import FarmTypeSelectModal from "./Modals/FarmTypeSelectModal";
import { 
  Button, 
  Box, 
  Typography, 
  Stepper, 
  Step, 
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Paper,
  Grid,
  TextField,
  Checkbox,
  InputAdornment,
  IconButton,
  Fade,
  Grow
} from "@mui/material";
import { 
  Person, 
  Business, 
  Agriculture, 
  School, 
  ArrowForward, 
  ArrowBack,
  Email,
  Phone,
  Lock,
  Visibility,
  VisibilityOff,
  LocationOn,
  ShoppingCart,
  Store,
  CalendarToday
} from "@mui/icons-material";

const FarmerRegistrationForm = () => {
  const navigate = useNavigate();
  const {
    isLoading,
    error,
    success,
    registeredUser,
    registerFarmerUser,
    resetState,
  } = useFarmerRegistration();

  // État pour suivre l'étape actuelle du formulaire
  const [activeStep, setActiveStep] = useState(0);
  
  // État pour suivre le type d'utilisateur (acheteur ou vendeur)
  const [userType, setUserType] = useState("");

  const [formData, setFormData] = useState({
    // États pour les modales
    provinceModalOpen: false,
    farmTypeModalOpen: false,
    
    // Type d'utilisateur
    userType: "",
    
    // Informations de base
    fullName: "",
    firstName: "",
    lastName: "",
    birthDate: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    showPassword: false,
    
    // Informations de la ferme (vendeur)
    farmName: "",
    farmLocation: "",
    crops: "",
    farmType: "maraichage", // Valeur par défaut
    
    // Informations coopérative (vendeur)
    cooperativeName: "",
    isCooperativeMember: false,
    cooperativeMembers: "0",
    
    // Expertise et terrain (vendeur)
    expertise: "",
    hasExpertise: false,
    hasPlot: false,
    plotSize: 0,
    province: "", // Aucune province sélectionnée par défaut
    
    // Conditions d'utilisation
    acceptTerms: false,
  });

  // Définition des étapes du formulaire
  const steps = [
    "Type d'utilisateur",
    "Informations personnelles",
    userType === "buyer" ? "Adresse" : "Informations de la ferme",
    userType === "buyer" ? "Confirmation" : "Expertise et coopérative",
    userType === "buyer" ? "" : "Confirmation"
  ].filter(step => step !== "");

  // Rediriger vers la page de vérification d'email après inscription réussie
  useEffect(() => {
    if (success && registeredUser) {
      // Redirection après un court délai
      const redirectTimer = setTimeout(() => {
        navigate("/email-verification");
      }, 1000);

      return () => clearTimeout(redirectTimer);
    }
  }, [success, registeredUser, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Pour les champs checkbox, utilisez la valeur 'checked'
    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked
      });
      return;
    }
    
    // Pour les champs numériques
    if (type === "number") {
      setFormData({
        ...formData,
        [name]: parseFloat(value) || 0
      });
      return;
    }
    
    // Pour tous les autres champs
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Gérer le changement de type d'utilisateur
  const handleUserTypeChange = (event) => {
    const type = event.target.value;
    setUserType(type);
    setFormData({
      ...formData,
      userType: type
    });
  };

  // Gérer l'affichage/masquage du mot de passe
  const handleTogglePasswordVisibility = () => {
    setFormData({
      ...formData,
      showPassword: !formData.showPassword
    });
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetState();
    
    // Vérification que la province est sélectionnée pour les vendeurs
    if (userType === "seller" && !formData.province) {
      alert("Veuillez sélectionner une province avant de soumettre le formulaire.");
      return;
    }
    
    // Formatage des données pour Firebase
    const formattedData = {
      ...formData,
      isCooperativeMember: formData.isCooperativeMember ? "oui" : "non",
      hasExpertise: formData.hasExpertise ? "oui" : "non",
      hasPlot: formData.hasPlot ? "oui" : "non",
    };
    
    await registerFarmerUser(formattedData);
  };
  
  // Importer les styles du loader
  useEffect(() => {
    import("../styles/Loader.css");
  }, []);

  // Vérifier si l'étape actuelle est valide pour passer à la suivante
  const isStepValid = () => {
    switch (activeStep) {
      case 0: // Type d'utilisateur
        return userType !== "";
      case 1: // Informations personnelles
        if (userType === "buyer") {
          return formData.firstName && formData.lastName && formData.phone && 
                 (formData.password.length >= 6) && 
                 (formData.password === formData.confirmPassword);
        } else {
          return formData.fullName && formData.email && formData.phone && 
                 (formData.password.length >= 6) && 
                 (formData.password === formData.confirmPassword);
        }
      case 2: // Informations ferme ou adresse
        if (userType === "buyer") {
          return formData.province !== "";
        } else {
          return formData.farmName && formData.farmLocation && formData.province !== "";
        }
      case 3: // Expertise ou confirmation
        return true; // Pas de validation stricte pour cette étape
      default:
        return true;
    }
  };

  // Rendu du contenu de l'étape actuelle
  const getStepContent = (step) => {
    switch (step) {
      case 0: // Type d'utilisateur
        return (
          <Grow in={activeStep === 0} timeout={500}>
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 4, color: '#014527', fontWeight: 'bold' }}>
                Que souhaitez-vous faire sur notre plateforme ?
              </Typography>
              
              <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} sm={6}>
                  <Paper 
                    elevation={userType === "buyer" ? 8 : 2}
                    sx={{
                      p: 3, 
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: userType === "buyer" ? '2px solid #014527' : '1px solid #ddd',
                      '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 }
                    }}
                    onClick={() => handleUserTypeChange({ target: { value: "buyer" } })}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <ShoppingCart sx={{ fontSize: 80, color: '#014527', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>Je souhaite acheter</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inscrivez-vous en tant qu'acheteur pour découvrir et acheter des produits agricoles de qualité
                      </Typography>
                      <Radio 
                        checked={userType === "buyer"}
                        onChange={handleUserTypeChange}
                        value="buyer"
                        name="userType"
                        sx={{ mt: 2 }}
                      />
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Paper 
                    elevation={userType === "seller" ? 8 : 2}
                    sx={{
                      p: 3, 
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: userType === "seller" ? '2px solid #014527' : '1px solid #ddd',
                      '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 }
                    }}
                    onClick={() => handleUserTypeChange({ target: { value: "seller" } })}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Store sx={{ fontSize: 80, color: '#014527', mb: 2 }} />
                      <Typography variant="h6" gutterBottom>Je souhaite vendre</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Inscrivez-vous en tant qu'agriculteur pour vendre vos produits et développer votre activité
                      </Typography>
                      <Radio 
                        checked={userType === "seller"}
                        onChange={handleUserTypeChange}
                        value="seller"
                        name="userType"
                        sx={{ mt: 2 }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Grow>
        );
        
      case 1: // Informations personnelles
        return (
          <Fade in={activeStep === 1} timeout={500}>
            <Box>
              {userType === "buyer" ? (
                // Formulaire pour les acheteurs
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Prénom"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nom"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Date de naissance"
                      name="birthDate"
                      type="date"
                      value={formData.birthDate}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarToday color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Téléphone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mot de passe"
                      name="password"
                      type={formData.showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      helperText="Au moins 6 caractères"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                            >
                              {formData.showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Confirmer le mot de passe"
                      name="confirmPassword"
                      type={formData.showPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                // Formulaire pour les vendeurs (agriculteurs)
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nom complet"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Téléphone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Mot de passe"
                      name="password"
                      type={formData.showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      helperText="Au moins 6 caractères"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="primary" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePasswordVisibility}
                              edge="end"
                            >
                              {formData.showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Confirmer le mot de passe"
                      name="confirmPassword"
                      type={formData.showPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Fade>
        );
        
      case 2: // Informations ferme ou adresse
        return (
          <Fade in={activeStep === 2} timeout={500}>
            <Box>
              {userType === "buyer" ? (
                // Adresse pour les acheteurs
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: '#014527' }}>
                        Votre localisation
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Ces informations nous aideront à vous proposer des produits disponibles dans votre région.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel id="province-label">Province</FormLabel>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setFormData((prevData) => ({ ...prevData, provinceModalOpen: true }))}
                        sx={{ 
                          textAlign: 'left', 
                          justifyContent: 'flex-start',
                          padding: '10px 12px',
                          color: formData.province ? 'text.primary' : 'text.secondary',
                          borderColor: '#ccc',
                          '&:hover': {
                            borderColor: '#999',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        startIcon={<LocationOn />}
                      >
                        {formData.province ? (
                          // Convertir la première lettre en majuscule et remplacer les tirets par des espaces
                          formData.province.charAt(0).toUpperCase() + 
                          formData.province.slice(1).replace(/-/g, ' ')
                        ) : (
                          "Sélectionnez une province"
                        )}
                      </Button>
                      
                      {/* Modal pour la sélection de province */}
                      <ProvinceSelectModal
                        open={formData.provinceModalOpen}
                        onClose={() => setFormData((prevData) => ({ ...prevData, provinceModalOpen: false }))}
                        onSelect={(provinceId) => {
                          setFormData((prevData) => ({
                            ...prevData,
                            province: provinceId,
                            provinceModalOpen: false
                          }));
                        }}
                        selectedValue={formData.province}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Adresse détaillée (optionnelle)"
                      name="farmLocation"
                      value={formData.farmLocation}
                      onChange={handleChange}
                      variant="outlined"
                      multiline
                      rows={2}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              ) : (
                // Informations de la ferme pour les vendeurs
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: '#014527' }}>
                        Informations de votre exploitation
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Ces informations nous aideront à présenter votre activité aux acheteurs potentiels.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nom de la ferme"
                      name="farmName"
                      value={formData.farmName}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Business color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Localisation"
                      name="farmLocation"
                      value={formData.farmLocation}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOn color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel id="province-label">Province</FormLabel>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setFormData((prevData) => ({ ...prevData, provinceModalOpen: true }))}
                        sx={{ 
                          textAlign: 'left', 
                          justifyContent: 'flex-start',
                          padding: '10px 12px',
                          color: formData.province ? 'text.primary' : 'text.secondary',
                          borderColor: '#ccc',
                          '&:hover': {
                            borderColor: '#999',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        startIcon={<LocationOn />}
                      >
                        {formData.province ? (
                          // Convertir la première lettre en majuscule et remplacer les tirets par des espaces
                          formData.province.charAt(0).toUpperCase() + 
                          formData.province.slice(1).replace(/-/g, ' ')
                        ) : (
                          "Sélectionnez une province"
                        )}
                      </Button>
                      
                      {/* Modal pour la sélection de province */}
                      <ProvinceSelectModal
                        open={formData.provinceModalOpen}
                        onClose={() => setFormData((prevData) => ({ ...prevData, provinceModalOpen: false }))}
                        onSelect={(provinceId) => {
                          setFormData((prevData) => ({
                            ...prevData,
                            province: provinceId,
                            provinceModalOpen: false
                          }));
                        }}
                        selectedValue={formData.province}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <FormLabel id="farm-type-label">Type d'exploitation</FormLabel>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => setFormData((prevData) => ({ ...prevData, farmTypeModalOpen: true }))}
                        sx={{ 
                          textAlign: 'left', 
                          justifyContent: 'flex-start',
                          padding: '10px 12px',
                          color: 'text.primary',
                          borderColor: '#ccc',
                          '&:hover': {
                            borderColor: '#999',
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        startIcon={<Agriculture />}
                      >
                        {formData.farmType === "maraichage" ? "Maraîchage" :
                         formData.farmType === "cereales" ? "Céréales" :
                         formData.farmType === "elevage" ? "Élevage" :
                         formData.farmType === "arboriculture" ? "Arboriculture" :
                         formData.farmType === "viticulture" ? "Viticulture" :
                         "Autre"}
                      </Button>
                      
                      {/* Modal pour la sélection du type d'exploitation */}
                      <FarmTypeSelectModal
                        open={formData.farmTypeModalOpen}
                        onClose={() => setFormData((prevData) => ({ ...prevData, farmTypeModalOpen: false }))}
                        onSelect={(typeId) => {
                          setFormData((prevData) => ({
                            ...prevData,
                            farmType: typeId,
                            farmTypeModalOpen: false
                          }));
                        }}
                        selectedValue={formData.farmType}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.hasPlot}
                          onChange={handleChange}
                          name="hasPlot"
                          color="primary"
                        />
                      }
                      label="J'ai un terrain"
                    />
                  </Grid>
                  {formData.hasPlot && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Taille du terrain (hectares)"
                        name="plotSize"
                        type="number"
                        value={formData.plotSize}
                        onChange={handleChange}
                        variant="outlined"
                        InputProps={{
                          inputProps: { min: 0, step: 0.1 }
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Cultures principales (séparées par des virgules)"
                      name="crops"
                      value={formData.crops}
                      onChange={handleChange}
                      variant="outlined"
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Fade>
        );
        
      case 3: // Expertise et coopérative ou confirmation
        return (
          <Fade in={activeStep === 3} timeout={500}>
            <Box>
              {userType === "buyer" ? (
                // Confirmation pour les acheteurs
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h5" gutterBottom sx={{ color: '#014527', mb: 3 }}>
                    Récapitulatif de vos informations
                  </Typography>
                  
                  <Paper elevation={3} sx={{ p: 3, mb: 3, maxWidth: 500, mx: 'auto', textAlign: 'left' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight="bold">Informations personnelles</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Prénom:</Typography>
                        <Typography variant="body1">{formData.firstName}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Nom:</Typography>
                        <Typography variant="body1">{formData.lastName}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Téléphone:</Typography>
                        <Typography variant="body1">{formData.phone}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Email:</Typography>
                        <Typography variant="body1">{formData.email || "Non renseigné"}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Date de naissance:</Typography>
                        <Typography variant="body1">{formData.birthDate}</Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">Localisation</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Province:</Typography>
                        <Typography variant="body1">
                          {formData.province ? formData.province.charAt(0).toUpperCase() + formData.province.slice(1).replace(/-/g, ' ') : ""}
                        </Typography>
                      </Grid>
                      {formData.farmLocation && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">Adresse détaillée:</Typography>
                          <Typography variant="body1">{formData.farmLocation}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.acceptTerms}
                        onChange={handleChange}
                        name="acceptTerms"
                        color="primary"
                        required
                      />
                    }
                    label={
                      <Typography variant="body2">
                        J'accepte les <a href="/terms" style={{ color: '#0066cc' }}>conditions d'utilisation</a>
                      </Typography>
                    }
                  />
                </Box>
              ) : (
                // Expertise et coopérative pour les vendeurs
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: '#014527' }}>
                        Expertise et coopérative
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Ces informations nous aideront à mieux comprendre votre activité et à vous mettre en relation avec d'autres agriculteurs.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.hasExpertise}
                          onChange={handleChange}
                          name="hasExpertise"
                          color="primary"
                        />
                      }
                      label="J'ai une expertise agricole"
                    />
                  </Grid>
                  {formData.hasExpertise && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description de l'expertise"
                        name="expertise"
                        value={formData.expertise}
                        onChange={handleChange}
                        variant="outlined"
                        multiline
                        rows={2}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <School color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isCooperativeMember}
                          onChange={handleChange}
                          name="isCooperativeMember"
                          color="primary"
                        />
                      }
                      label="Je suis membre d'une coopérative"
                    />
                  </Grid>
                  {formData.isCooperativeMember && (
                    <>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="Nom de la coopérative"
                          name="cooperativeName"
                          value={formData.cooperativeName}
                          onChange={handleChange}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Nombre de membres"
                          name="cooperativeMembers"
                          type="number"
                          value={formData.cooperativeMembers}
                          onChange={handleChange}
                          variant="outlined"
                          InputProps={{
                            inputProps: { min: 0 }
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </Box>
          </Fade>
        );
        
      case 4: // Confirmation pour les vendeurs
        return (
          <Fade in={activeStep === 4} timeout={500}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" gutterBottom sx={{ color: '#014527', mb: 3 }}>
                Récapitulatif de vos informations
              </Typography>
              
              <Paper elevation={3} sx={{ p: 3, mb: 3, maxWidth: 500, mx: 'auto', textAlign: 'left' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold">Informations personnelles</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Nom complet:</Typography>
                    <Typography variant="body1">{formData.fullName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Email:</Typography>
                    <Typography variant="body1">{formData.email}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Téléphone:</Typography>
                    <Typography variant="body1">{formData.phone}</Typography>
                  </Grid>
                  
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold">Informations de la ferme</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Nom de la ferme:</Typography>
                    <Typography variant="body1">{formData.farmName}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Localisation:</Typography>
                    <Typography variant="body1">{formData.farmLocation}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Province:</Typography>
                    <Typography variant="body1">
                      {formData.province ? formData.province.charAt(0).toUpperCase() + formData.province.slice(1).replace(/-/g, ' ') : ""}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Type d'exploitation:</Typography>
                    <Typography variant="body1">
                      {formData.farmType === "maraichage" ? "Maraîchage" :
                       formData.farmType === "cereales" ? "Céréales" :
                       formData.farmType === "elevage" ? "Élevage" :
                       formData.farmType === "arboriculture" ? "Arboriculture" :
                       formData.farmType === "viticulture" ? "Viticulture" :
                       "Autre"}
                    </Typography>
                  </Grid>
                  
                  {formData.hasExpertise && (
                    <>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">Expertise</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">Description:</Typography>
                        <Typography variant="body1">{formData.expertise}</Typography>
                      </Grid>
                    </>
                  )}
                  
                  {formData.isCooperativeMember && (
                    <>
                      <Grid item xs={12} sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">Coopérative</Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" color="text.secondary">Nom:</Typography>
                        <Typography variant="body1">{formData.cooperativeName}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">Membres:</Typography>
                        <Typography variant="body1">{formData.cooperativeMembers}</Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Paper>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.acceptTerms}
                    onChange={handleChange}
                    name="acceptTerms"
                    color="primary"
                    required
                  />
                }
                label={
                  <Typography variant="body2">
                    J'accepte les <a href="/terms" style={{ color: '#0066cc' }}>conditions d'utilisation</a>
                  </Typography>
                }
              />
            </Box>
          </Fade>
        );
        
      default:
        return "Étape inconnue";
    }
  };

  return (
    <div id="registration" className="form-container">
      <Box sx={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.9)', 
        borderRadius: '10px',
        p: 3,
        boxShadow: 3
      }}>
        <Typography variant="h4" component="h1" align="center" gutterBottom sx={{ 
          color: '#014527',
          fontWeight: 'bold',
          position: 'relative',
          pb: 2,
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '80px',
            height: '4px',
            backgroundColor: '#28a745',
            borderRadius: '2px'
          }
        }}>
          {userType === "buyer" ? "Inscription Acheteur" : 
           userType === "seller" ? "Inscription Agriculteur" : 
           "Inscription"}
        </Typography>

        {success && (
          <Box className="success-message" sx={{ mb: 3 }}>
            <Typography variant="body1">
              Inscription réussie! Un email de vérification a été envoyé à {formData.email || formData.phone}.
            </Typography>
            <Typography variant="body1">
              Vous allez être redirigé vers la page de vérification...
            </Typography>
          </Box>
        )}

        <Stepper 
          activeStep={activeStep} 
          alternativeLabel 
          sx={{ 
            mb: 4, 
            mt: 3,
            '& .MuiStepLabel-root': {
              '@media (max-width: 600px)': {
                '& .MuiStepLabel-labelContainer': {
                  width: 0,
                  overflow: 'hidden',
                  transition: 'width 0.3s ease',
                  '&.Mui-active': {
                    width: 'auto',
                    maxWidth: '120px',
                    overflow: 'visible'
                  }
                },
                '& .MuiStepIcon-root': {
                  fontSize: '1.2rem',
                  '&.Mui-active': {
                    fontSize: '1.5rem'
                  }
                }
              }
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel 
                classes={{
                  labelContainer: activeStep === steps.indexOf(label) ? 'Mui-active' : ''
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit}>
          {getStepContent(activeStep)}
          
          {error && <Box className="error-message" sx={{ mt: 2, mb: 2 }}>{error}</Box>}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid #eee' }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBack />}
              sx={{ mr: 1 }}
            >
              Retour
            </Button>
            <Box sx={{ flex: '1 1 auto' }} />
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isLoading || !formData.acceptTerms}
                sx={{ 
                  bgcolor: '#014527',
                  '&:hover': { bgcolor: '#035417' },
                  minWidth: '120px'
                }}
              >
                {isLoading ? <div className="loader"></div> : "S'inscrire"}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                endIcon={<ArrowForward />}
                disabled={!isStepValid()}
                sx={{ 
                  bgcolor: '#014527',
                  '&:hover': { bgcolor: '#035417' }
                }}
              >
                Suivant
              </Button>
            )}
          </Box>
          
          {activeStep === 0 && (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Typography variant="body2">
                Déjà inscrit? <a href="/farmer-login" style={{ color: '#0066cc' }}>Connectez-vous</a>
              </Typography>
            </Box>
          )}
        </form>
        
        {/* Overlay de chargement qui s'affiche sur toute la page */}
        {isLoading && (
          <div className="loader-overlay">
            <div className="loader"></div>
          </div>
        )}
      </Box>
    </div>
  );
};

export default FarmerRegistrationForm;